import { api } from './client';
import type { ContactMailStatus } from './mailing';

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'won' | 'lost' | 'rejected';
export type LeadKind = 'affiliate' | 'inzerent';

export interface LeadChannel {
  id: number;
  position: number;
  url: string;
  host: string;
  finalUrl: string | null;
  title: string | null;
  description: string | null;
  siteName: string | null;
  faviconUrl: string | null;
  httpStatus: number | null;
  fetchStatus: 'pending' | 'ok' | 'failed';
  fetchError: string | null;
  fetchedAt: string | null;
}

export interface LeadListItem {
  brand: string | null;
  extendsPartnerId: number | null;
  extendsPartnerName: string | null;
  id: number;
  firstName: string;
  lastName: string;
  vocative: string | null;
  company: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  status: LeadStatus;
  kind: LeadKind;
  focus: string | null;
  product: string | null;
  platform: string | null;
  affiliatePlatform: string | null;
  module: string | null;
  services: string[];
  channels: LeadChannel[];
  formId: string | null;
  lang: string | null;
  assignedToUserId: number | null;
  assignedToName: string | null;
  expectedMonthlyEur: number | null;
  sourceHost: string | null;
  createdAt: string;
  confirmedAt?: string | null;
  registrationSentAt?: string | null;
  registrationClickedAt?: string | null;
  convertedAt?: string | null;
  introCallAt?: string | null;
  offerSentAt?: string | null;
  contractSentAt?: string | null;
  clientPartnerId?: number | null;
  affiliatePaired?: boolean;
}

export interface SpaceUser {
  id: number;
  name: string;
  email: string | null;
}

export interface LeadNote {
  id: number;
  body: string;
  authorUserId: number | null;
  authorName: string | null;
  createdAt: string;
}

export interface LeadDetail extends LeadListItem {
  ic: string | null;
  dic: string | null;
  gdprConsentAt: string | null;
  note: string | null;
  sourceUrl: string | null;
  sourceIp: string | null;
  confirmedAt: string | null;
  doiSentAt: string | null;
  doiOpenedAt: string | null;
  doiBouncedAt: string | null;
  registrationSentAt: string | null;
  registrationOpenedAt: string | null;
  registrationClickedAt: string | null;
  registrationBouncedAt: string | null;
  emailStatus?: ContactMailStatus | null;
  convertedAt: string | null;
  introCallAt: string | null;
  offerSentAt: string | null;
  offerUrl: string | null;
  clientPartnerId: number | null;
  updatedAt: string;
}

export interface LeadAffiliateLink {
  papAffiliateId: string;
  paired: boolean;
  displayName: string | null;
}

export interface LeadInput {
  firstName: string;
  lastName: string;
  vocative?: string;
  lang?: string | null;
  kind?: LeadKind;
  company?: string | null;
  brand?: string | null;
  focus?: string | null;
  product?: string | null;
  platform?: string | null;
  affiliatePlatform?: string | null;
  module?: string | null;
  extendsPartnerId?: number | null;
  services?: string[];
  channels?: string[];
  website?: string | null;
  ic?: string | null;
  dic?: string | null;
  email?: string | null;
  phone?: string | null;
  note?: string | null;
}

export function listLeads(params: {
  search?: string;
  status?: LeadStatus | 'active' | 'open';
  kind?: LeadKind;
  lang?: string;
  service?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: LeadListItem[]; total: number }> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.status) qs.set('status', params.status);
  if (params.kind) qs.set('kind', params.kind);
  if (params.lang) qs.set('lang', params.lang);
  if (params.service) qs.set('service', params.service);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return api.get(`/api/v1/crm/leads${q ? `?${q}` : ''}`);
}

export function clearLeadEmailStatus(id: number): Promise<{ cleared: boolean }> {
  return api.post(`/api/v1/crm/leads/${id}/email-status/clear`, {});
}

export function sendAffiliateRegistration(id: number): Promise<{ emailSent: boolean }> {
  return api.post(`/api/v1/crm/leads/${id}/send-registration`, {});
}

export function convertLeadToAffiliate(id: number, market: 'CZ' | 'SK' | null): Promise<{ papAffiliateId: string; created: boolean }> {
  return api.post(`/api/v1/crm/leads/${id}/convert-affiliate`, market ? { market } : {});
}

export function getLeadAffiliate(id: number): Promise<LeadAffiliateLink | null> {
  return api.get(`/api/v1/crm/leads/${id}/affiliate`);
}

export function createPartnerFromLead(id: number, market: 'CZ' | 'SK' | 'FOREIGN'): Promise<{ partnerId: number; created: boolean }> {
  return api.post(`/api/v1/crm/leads/${id}/create-partner`, { market });
}

export function getLead(id: number): Promise<LeadDetail> {
  return api.get(`/api/v1/crm/leads/${id}`);
}

export function createLead(input: LeadInput): Promise<{ id: number }> {
  return api.post('/api/v1/crm/leads', input);
}

export function addLeadChannel(id: number, url: string): Promise<LeadChannel[]> {
  return api.post(`/api/v1/crm/leads/${id}/channels`, { url });
}

export function deleteLeadChannel(id: number, channelId: number): Promise<LeadChannel[]> {
  return api.post(`/api/v1/crm/leads/${id}/channels/${channelId}/delete`, {});
}

export function refreshLeadChannel(id: number, channelId: number): Promise<LeadChannel[]> {
  return api.post(`/api/v1/crm/leads/${id}/channels/${channelId}/refresh`, {});
}

export function setLeadStatus(id: number, status: LeadStatus): Promise<unknown> {
  return api.post(`/api/v1/crm/leads/${id}/status`, { status });
}

export function deleteLead(id: number): Promise<unknown> {
  return api.post(`/api/v1/crm/leads/${id}/delete`, {});
}

export function updateLead(
  id: number,
  patch: {
    note?: string | null;
    lang?: string | null;
    assignedToUserId?: number | null;
    expectedMonthlyEur?: number | null;
    kind?: LeadKind;
    firstName?: string;
    lastName?: string;
    vocative?: string | null;
    company?: string | null;
    brand?: string | null;
    focus?: string | null;
    product?: string | null;
    platform?: string | null;
    affiliatePlatform?: string | null;
    module?: string | null;
    services?: string[];
    website?: string | null;
    ic?: string | null;
    dic?: string | null;
    email?: string | null;
    phone?: string | null;
  },
): Promise<unknown> {
  return api.post(`/api/v1/crm/leads/${id}/update`, patch);
}

export interface SwitchKindResult {
  kind: LeadKind;
  partnerId?: number;
  partnerCreated?: boolean;
  contactId?: number | null;
  market?: 'CZ' | 'SK' | 'FOREIGN';
  emailSent: boolean;
  emailReason?: string | null;
  dealId?: number;
  dealCreated?: boolean;
  dealAmount?: number | null;
  dealCurrency?: 'CZK' | 'EUR' | null;
}

export function switchLeadKind(id: number, kind: LeadKind): Promise<SwitchKindResult> {
  return api.post(`/api/v1/crm/leads/${id}/switch-kind`, { kind });
}

export function listAssignees(): Promise<SpaceUser[]> {
  return api.get('/api/v1/crm/assignees');
}

export function listLeadNotes(id: number): Promise<LeadNote[]> {
  return api.get(`/api/v1/crm/leads/${id}/notes`);
}

export function addLeadNote(id: number, body: string): Promise<LeadNote> {
  return api.post(`/api/v1/crm/leads/${id}/notes`, { body });
}

export interface MailTemplateListItem {
  id: number;
  name: string;
  lang: string;
  subject: string;
  purpose: string | null;
  enabled: boolean;
  createdAt: string;
}
export interface MailTemplateDetail extends MailTemplateListItem {
  bodyHtml: string | null;
  bodyPlain: string | null;
  updatedAt: string;
}
export interface MailTemplateInput {
  name?: string;
  lang?: string;
  subject?: string;
  purpose?: string | null;
  bodyHtml?: string | null;
  bodyPlain?: string | null;
}

export function listMailTemplates(all = false): Promise<MailTemplateListItem[]> {
  return api.get(`/api/v1/crm/templates${all ? '?all=1' : ''}`);
}
export function getMailTemplate(id: number): Promise<MailTemplateDetail> {
  return api.get(`/api/v1/crm/templates/${id}`);
}
export function createMailTemplate(input: MailTemplateInput): Promise<{ id: number }> {
  return api.post('/api/v1/crm/templates', input);
}
export function updateMailTemplate(id: number, input: MailTemplateInput): Promise<unknown> {
  return api.put(`/api/v1/crm/templates/${id}`, input);
}
export function archiveMailTemplate(id: number): Promise<unknown> {
  return api.post(`/api/v1/crm/templates/${id}/archive`, {});
}
export function restoreMailTemplate(id: number): Promise<unknown> {
  return api.post(`/api/v1/crm/templates/${id}/restore`, {});
}

export interface MailerStatus {
  provider: string;
  configured: boolean;
  fromName: string;
  fromEmail: string | null;
}
export function getMailerStatus(): Promise<MailerStatus> {
  return api.get('/api/v1/crm/templates/mailer/status');
}
export function sendMailerTest(to: string): Promise<unknown> {
  return api.post('/api/v1/crm/templates/mailer/test', { to });
}

export interface CrmStatsMonth {
  ym: string;
  total: number;
  affiliate: number;
  inzerent: number;
  won: number;
  value: number;
  wonValue: number;
}

export interface CrmStats {
  total: number;
  won: number;
  wonStatus: LeadStatus;
  pipelineValue: number;
  wonValue: number;
  tableValue?: number;
  closedValue?: number;
  byStatus: Record<string, number>;
  byKind: Record<string, { total: number; won: number; value: number; wonValue: number }>;
  months: CrmStatsMonth[];
}

export interface CrmKindStats {
  kind: LeadKind;
  total: number;
  confirmed: number;
  rejected: number;
  tableValue: number;
  confirmedValue: number;
  byStatus: Record<string, number>;
  funnel: { key: string; count: number }[];
  products: { key: string; count: number; value: number }[];
  focus: { key: string; count: number }[];
  langs: { key: string; count: number }[];
  months: { ym: string; total: number; confirmed: number; value: number; confirmedValue: number }[];
}

export function getCrmKindStats(kind: LeadKind, months: number | 'all'): Promise<CrmKindStats> {
  return api.get(`/api/v1/crm/stats/kind?kind=${kind}&months=${months}`);
}

export interface CrmClientHit {
  id: number;
  name: string;
  ic: string | null;
  vat: string | null;
  website: string | null;
}

export function searchCrmClients(q: string): Promise<CrmClientHit[]> {
  return api.get(`/api/v1/crm/clients?q=${encodeURIComponent(q)}`);
}

export interface CrmPlatformSuggestions {
  platforms: string[];
  affiliatePlatforms: string[];
}

export function getCrmPlatformSuggestions(): Promise<CrmPlatformSuggestions> {
  return api.get('/api/v1/crm/platform-suggestions');
}

export function getCrmStats(months: number | 'all'): Promise<CrmStats> {
  return api.get(`/api/v1/crm/stats?months=${months}`);
}

export function markIntroCall(id: number): Promise<unknown> {
  return api.post(`/api/v1/crm/leads/${id}/intro-call`, {});
}

export function markOfferSent(id: number, url: string | null): Promise<unknown> {
  return api.post(`/api/v1/crm/leads/${id}/offer`, { url });
}

export function markContractSent(id: number): Promise<unknown> {
  return api.post(`/api/v1/crm/leads/${id}/contract-sent`, {});
}

export function getLeadClient(id: number): Promise<{ partnerId: number; name: string } | null> {
  return api.get(`/api/v1/crm/leads/${id}/client`);
}

export type { AffiliateSocials as LeadSocials, AffiliateSocialProfile as LeadSocialProfile } from './affilmaster';
export async function getLeadSocials(leadId: number) {
  return api.get<import('./affilmaster').AffiliateSocials>(`/api/v1/crm/leads/${leadId}/socials`);
}
export async function refreshLeadSocials(leadId: number, force = false) {
  return api.post<import('./affilmaster').AffiliateSocials>(`/api/v1/crm/leads/${leadId}/socials/refresh${force ? '?force=1' : ''}`);
}
